import {
  TYPE_RESPONSE_INVOICING_QUANTITY,
  TYPE_RESPONSE_PAYMENT_MODE,
  TYPE_RESPONSE_PAYMENT_CURRENCY,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM,
  TYPE_RESPONSE_LAW,
  TYPE_RESPONSE_ARBITRATION,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION,
  TYPE_RESPONSE_ADDITIONAL_NOTES,
  TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
  TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
  TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
} from '../respond/helpers';
import { isSameHtml, isSameId } from './helpers';

// Satu thread respond + counter + Agree & Lock per field Payment Terms /
// Compliance. `field` = kolom di procurement & row counter; `entity` = objek
// master (choice). Tanpa entity = freetext (HTML). `agreeTypeResponse` =
// baris agreement (PUT agree-field): choice = type sendiri, free-text 19-21.
export const PAYMENT_TERM_FIELDS = [
  {
    key: 'invoicingQuantity',
    typeResponse: TYPE_RESPONSE_INVOICING_QUANTITY,
    agreeTypeResponse: TYPE_RESPONSE_INVOICING_QUANTITY,
    label: 'Invoicing Quantity',
    field: 'invoicingQuantityId',
    entity: 'invoicingQuantity',
  },
  {
    key: 'paymentMode',
    typeResponse: TYPE_RESPONSE_PAYMENT_MODE,
    agreeTypeResponse: TYPE_RESPONSE_PAYMENT_MODE,
    label: 'Mode',
    field: 'modeInvoicingQuantityId',
    entity: 'modeInvoicingQuantity',
  },
  {
    key: 'paymentCurrency',
    typeResponse: TYPE_RESPONSE_PAYMENT_CURRENCY,
    agreeTypeResponse: TYPE_RESPONSE_PAYMENT_CURRENCY,
    label: 'Currency',
    field: 'paymentCurrencyId',
    entity: 'paymentCurrency',
  },
  {
    key: 'descriptionOfPaymentTerm',
    typeResponse: TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM,
    agreeTypeResponse: TYPE_RESPONSE_DESCRIPTION_OF_PAYMENT_TERM_AGREED,
    label: 'Description of Payment Term',
    field: 'descriptionOfPaymentTerms',
  },
];

export const COMPLIANCE_FIELDS = [
  {
    key: 'law',
    typeResponse: TYPE_RESPONSE_LAW,
    agreeTypeResponse: TYPE_RESPONSE_LAW,
    label: 'Law',
    field: 'lawId',
    entity: 'law',
  },
  {
    key: 'arbitration',
    typeResponse: TYPE_RESPONSE_ARBITRATION,
    agreeTypeResponse: TYPE_RESPONSE_ARBITRATION,
    label: 'Arbitration',
    field: 'arbitrationId',
    entity: 'arbitration',
  },
  {
    key: 'otherTermsAndCondition',
    typeResponse: TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION,
    agreeTypeResponse: TYPE_RESPONSE_OTHER_TERMS_AND_CONDITION_AGREED,
    label: 'Other Terms & Condition',
    field: 'otherTermsAndCondition',
  },
  {
    key: 'additionalNotes',
    typeResponse: TYPE_RESPONSE_ADDITIONAL_NOTES,
    agreeTypeResponse: TYPE_RESPONSE_ADDITIONAL_NOTES_AGREED,
    label: 'Additional Notes',
    field: 'additionalNotes',
  },
];

export const PAYMENT_COMPLIANCE_FIELDS = [
  ...PAYMENT_TERM_FIELDS,
  ...COMPLIANCE_FIELDS,
];

export const isFreeTextField = def => !def?.entity;

// Nilai field dari procurement / row counter (choice → id, freetext → HTML).
export const getCounterFieldValue = (source, def) => {
  if (!source || !def) return '';
  if (isFreeTextField(def)) return source[def.field] ?? '';
  const entity = source[def.entity];
  return source[def.field] || entity?.id || entity?.Id || '';
};

const toNullable = value =>
  value == null || String(value).trim() === '' ? null : value;

// Row counter lengkap: basis = counter terakhir Mitra (atau original
// procurement), hanya `changedDef` yang diganti — field lain tetap.
export const buildFieldCounterRow = (
  defs,
  latestCounter,
  procurement,
  changedDef,
  value
) =>
  defs.reduce((row, def) => {
    const next =
      def.field === changedDef?.field
        ? value
        : getCounterFieldValue(latestCounter || procurement, def);
    row[def.field] = toNullable(next);
    return row;
  }, {});

// Field pilihan (Invoicing/Mode/Currency, Law/Arbitration): satu thread
// kelompok (type 5/6) + Agree & Lock per field (baris agreement type 8-13).
// Field free-text tetap satu thread per field.
export const isGroupAgreeField = def => !isFreeTextField(def);

// Bisa di-agree hanya jika counter terakhir Mitra beda dari original
// (choice = id, free-text = HTML dinormalisasi).
export const isFieldAgreeable = (def, latestCounter, procurement) => {
  if (!def || !latestCounter) return false;
  if (isFreeTextField(def)) {
    return !isSameHtml(latestCounter[def.field], procurement?.[def.field]);
  }
  return !isSameId(
    latestCounter[def.field],
    procurement?.[def.field],
    latestCounter[def.entity],
    procurement?.[def.entity]
  );
};

export const isFieldCountered = isFieldAgreeable;

export const hasAnyFieldCountered = (defs, latestCounter, procurement) =>
  (defs ?? []).some(def => isFieldCountered(def, latestCounter, procurement));

const findAgreedRow = rows =>
  (Array.isArray(rows) ? rows : []).find(
    item => item?.isLocked === true && String(item?.finalValue ?? '').trim()
  );

// Free-text (19-21 / lock thread lama): Final = HTML apa adanya.
export const getAgreedFinalHtml = rows =>
  findAgreedRow(rows)?.finalValue ?? null;

// Final free-text: agreement 19-21, fallback lock lama di thread 11/14/15.
export const getFreeTextAgreedFinal = (def, responsesByType) =>
  getAgreedFinalHtml(responsesByType?.[def?.agreeTypeResponse]) ??
  getAgreedFinalHtml(responsesByType?.[def?.typeResponse]);

// Baris agreement aktif → label Final (BE HTML-encode → decode ke teks).
export const getAgreedFinal = rows => {
  const row = findAgreedRow(rows);
  if (!row) return null;
  const doc = new DOMParser().parseFromString(
    String(row.finalValue),
    'text/html'
  );
  return (doc.documentElement.textContent || '').trim() || null;
};

// Thread kelompok tanpa Agree level thread: selesai bila ada field yang
// dicounter dan semuanya sudah di-agree. items = [{ agreed, agreeable }].
export const isCounterGroupAgreed = items => {
  const relevant = (items ?? []).filter(item => item.agreed || item.agreeable);
  return relevant.length > 0 && relevant.every(item => item.agreed);
};
